import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { Button, Input, Alert } from '../../components/common';
import './AuthPages.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: 输入邮箱, 2: 填写信息
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const allowedDomain = 'turenscape.com';

  const [formData, setFormData] = useState({
    email: '',
    code: '',
    username: '',
    password: '',
    passwordConfirm: '',
    realName: '',
    phone: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: '', text: '' });
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      setMessage({ type: 'error', text: '请输入邮箱地址' });
      return;
    }

    // 简单的邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: '请输入有效的邮箱地址' });
      return;
    }

    const emailLc = formData.email.trim().toLowerCase();
    if (!emailLc.endsWith(`@${allowedDomain}`)) {
      setMessage({ type: 'error', text: `仅支持使用 @${allowedDomain} 邮箱注册` });
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.requestRegisterCode(formData.email);
      setMessage({ type: 'success', text: response.data.message || '验证码已发送' });
      setStep(2);

      // 开发环境显示验证码
      if (response.data.debug_code) {
        setMessage({
          type: 'info',
          text: `验证码已发送。开发模式验证码：${response.data.debug_code}`,
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.email?.[0] ||
        err.response?.data?.detail ||
        '发送验证码失败，请稍后重试';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // 表单验证
    if (!formData.code) {
      setMessage({ type: 'error', text: '请输入验证码' });
      return;
    }

    if (!formData.username) {
      setMessage({ type: 'error', text: '请输入用户名' });
      return;
    }

    if (formData.username.length < 3) {
      setMessage({ type: 'error', text: '用户名至少3个字符' });
      return;
    }

    if (!formData.password || !formData.passwordConfirm) {
      setMessage({ type: 'error', text: '请输入密码' });
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setMessage({ type: 'error', text: '两次输入的密码不一致' });
      return;
    }

    if (formData.password.length < 8) {
      setMessage({ type: 'error', text: '密码长度至少8位' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.confirmRegister({
        email: formData.email,
        code: formData.code,
        username: formData.username,
        password: formData.password,
        password_confirm: formData.passwordConfirm,
        real_name: formData.realName,
        phone: formData.phone,
      });
      setMessage({ type: 'success', text: '注册成功！即将跳转到登录页面...' });
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const errorData = err.response?.data;
      let errorMsg = '注册失败，请稍后重试';
      
      if (errorData) {
        if (errorData.code) errorMsg = errorData.code[0];
        else if (errorData.username) errorMsg = errorData.username[0];
        else if (errorData.password) errorMsg = errorData.password[0];
        else if (errorData.email) errorMsg = errorData.email[0];
        else if (errorData.phone) errorMsg = errorData.phone[0];
        else if (errorData.detail) errorMsg = errorData.detail;
      }
      
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <div className="auth-bg-shape auth-bg-shape-1"></div>
        <div className="auth-bg-shape auth-bg-shape-2"></div>
        <div className="auth-bg-shape auth-bg-shape-3"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card animate-slideUp">
          <div className="auth-header">
            <div className="auth-logo">
              <svg viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="10" fill="url(#logo-gradient)" />
                <path
                  d="M20 10L28 26H12L20 10Z"
                  fill="white"
                  fillOpacity="0.9"
                />
                <circle cx="20" cy="28" r="3" fill="white" />
                <defs>
                  <linearGradient id="logo-gradient" x1="0" y1="0" x2="40" y2="40">
                    <stop stopColor="#0d9488" />
                    <stop offset="1" stopColor="#0f766e" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h1 className="auth-title">创建账号</h1>
            <p className="auth-subtitle">
              {step === 1
                ? '输入您的邮箱地址开始注册'
                : '完善您的账号信息'}
            </p>
          </div>

          {/* Step indicators */}
          <div className="auth-steps">
            <div className={`auth-step ${step >= 1 ? 'active' : ''}`}>
              <span className="auth-step-number">1</span>
              <span className="auth-step-label">验证邮箱</span>
            </div>
            <div className="auth-step-line"></div>
            <div className={`auth-step ${step >= 2 ? 'active' : ''}`}>
              <span className="auth-step-number">2</span>
              <span className="auth-step-label">完善信息</span>
            </div>
          </div>

          {message.text && (
            <Alert
              type={message.type}
              message={message.text}
              onClose={() => setMessage({ type: '', text: '' })}
            />
          )}

          {step === 1 ? (
            <form className="auth-form" onSubmit={handleRequestCode}>
              <Input
                name="email"
                type="email"
                label="邮箱地址"
                placeholder={`请输入您的土人邮箱`}
                value={formData.email}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                }
              />

              <Button type="submit" fullWidth loading={loading}>
                发送验证码
              </Button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleRegister}>
              <Input
                name="code"
                label="验证码"
                placeholder="请输入6位验证码"
                value={formData.code}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />

              <Input
                name="username"
                label="用户名"
                placeholder="请输入用户名（至少3个字符）"
                value={formData.username}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                }
              />

              <Input
                name="realName"
                label="真实姓名（可选）"
                placeholder="请输入真实姓名"
                value={formData.realName}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                }
              />

              <Input
                name="phone"
                label="手机号（可选）"
                placeholder="请输入手机号"
                value={formData.phone}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                }
              />

              <Input
                name="password"
                type="password"
                label="密码"
                placeholder="请输入密码（至少8位）"
                value={formData.password}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />

              <Input
                name="passwordConfirm"
                type="password"
                label="确认密码"
                placeholder="请再次输入密码"
                value={formData.passwordConfirm}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />

              <div className="auth-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setStep(1)}
                >
                  返回
                </Button>
                <Button type="submit" loading={loading}>
                  注册
                </Button>
              </div>
            </form>
          )}

          <div className="auth-footer">
            <p>
              已有账号？
              <Link to="/login" className="auth-link" style={{ marginLeft: '0.5rem' }}>
                立即登录
              </Link>
            </p>
          </div>
        </div>

        <p className="auth-copyright">
          © 2024 账号管理系统. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;

